<template>
  <div class="hypervisor-sync-results">
    <div v-if="loading" class="hypervisor-sync-results-overlay">
      <div class="hypervisor-sync-results-loader">
        <span class="hypervisor-sync-results-spinner" aria-hidden="true"></span>
        <span>Loading sync results...</span>
      </div>
    </div>

    <div class="sync-filter-bar network-tabs-nav" role="tablist" aria-label="Sync diff filters">
      <button
        v-for="option in filterOptions"
        :key="`sync-filter-${option.value}`"
        type="button"
        class="network-tab"
        :class="{ 'is-active': isFilterActive(option.value) }"
        :aria-selected="isFilterActive(option.value) ? 'true' : 'false'"
        @click="toggleFilter(option.value)">
        {{ option.label }}
      </button>
    </div>

    <table class="table table-striped table-bordered table-condensed" id="hypervisor-sync-table">
      <thead>
        <tr>
          <th><div class="text-center">OS</div></th>
          <th><div class="text-center">Name</div></th>
          <th><div class="text-center">Owner</div></th>
          <th><div class="text-center">Status in DB</div></th>
          <th><div class="text-center">Status in Actual</div></th>
          <th><div class="text-center">HyperVisor in DB</div></th>
          <th><div class="text-center">HyperVisor in Actual</div></th>
          <th><div class="text-center">Diff</div></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in visibleRows" :key="`sync-${row.id}`" :data-sync-diff="row.diff_value">
          <td><AppOsIcon :symbol="row.os_icon_symbol" /></td>
          <td>{{ row.name }}</td>
          <td>{{ row.owner }}</td>
          <td class="text-center"><span class="app-status-chip" :class="row.db_status_class">{{ row.db_status_label }}</span></td>
          <td class="text-center"><span class="app-status-chip" :class="row.actual_status_class">{{ row.actual_status_label }}</span></td>
          <td>{{ row.db_hv_name }}</td>
          <td>{{ row.actual_hv_name }}</td>
          <td class="text-center"><span class="app-status-chip" :class="row.diff_class">{{ row.diff_value }}</span></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import AppOsIcon from './AppOsIcon.vue'

const props = defineProps({
  endpoint: {
    type: String,
    default: ''
  },
  initialData: {
    type: Object,
    default: () => ({ rows: [] })
  }
})

const filterOptions = [
  { label: 'All', value: 'all' },
  { label: 'SYNCED', value: 'SYNCED' },
  { label: 'MOVED', value: 'MOVED' },
  { label: 'MISMATCH', value: 'MISMATCH' },
  { label: 'NOT FOUND', value: 'NOT FOUND' }
]

const selectedFilters = ref([])
const loading = ref(!(props.initialData?.rows || []).length)
const rowsState = ref(props.initialData?.rows || [])
const rows = computed(() => rowsState.value || [])
const visibleRows = computed(() => {
  if (!selectedFilters.value.length) {
    return rows.value
  }

  return rows.value.filter((row) => selectedFilters.value.includes(row.diff_value))
})

async function fetchResults() {
  loading.value = true

  try {
    const response = await fetch(props.endpoint, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    })

    if (!response.ok) throw new Error('Failed to load sync results.')

    const data = await response.json()
    rowsState.value = data?.results_data?.rows || []
  } catch (error) {
    console.error(error)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  if (props.endpoint) {
    fetchResults()
  } else {
    loading.value = false
  }
})

function isFilterActive(value) {
  if (value === 'all') {
    return selectedFilters.value.length === 0
  }

  return selectedFilters.value.includes(value)
}

function toggleFilter(value) {
  if (value === 'all') {
    selectedFilters.value = []
    return
  }

  if (selectedFilters.value.includes(value)) {
    selectedFilters.value = selectedFilters.value.filter((item) => item !== value)
    return
  }

  selectedFilters.value = [...selectedFilters.value, value]
}
</script>

<style scoped>
.hypervisor-sync-results {
  position: relative;
  min-height: 120px;
}

.hypervisor-sync-results-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(248, 250, 253, 0.82);
  backdrop-filter: blur(1px);
}

.hypervisor-sync-results-loader {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border: 1px solid rgba(130, 150, 175, 0.24);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  color: #36506a;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 16px 34px rgba(122, 141, 164, 0.16);
}

.hypervisor-sync-results-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(116, 143, 171, 0.22);
  border-top-color: #4d6d8f;
  border-radius: 999px;
  animation: hypervisor-sync-spin 0.9s linear infinite;
}

@keyframes hypervisor-sync-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
